<template>

	<div>
		
		<div class="d-flex flex-row mb-4">
			<div class="headline font-weight-medium pt-0">{{field.name}}</div>
			<v-spacer/>
			<v-select
				class="mx-5"
				:items="field.template.typeProperties.fieldTypesAvailable"
				v-model="fieldTypeToAdd"
				item-text="name"
				return-object
				label="Field type"
				required
				outlined
				dense
				style="max-width: 230px;"
			></v-select>
			<v-btn style="height: 40px;" width="120" small color="secondary" @click="addEntry">
				Add component
			</v-btn>
		</div>



		<!-- -------------------------------------------- -->
		<!--   Fields expansion panel -->
		<!-- -------------------------------------------- -->

		<v-expansion-panels multiple class="mx-0 pr-0 mb-6">
			
			<!-- -------------------------------------------- -->
			<!--   Field entries in expansion panel -->
			<!-- -------------------------------------------- -->

			<v-expansion-panel v-for="(entry, i) in components" :key="i">

				<!-- Expanded panel header-->
				<v-expansion-panel-header color="grey lighten-4 primary--text text--darken-1">
					<div>
						<v-btn v-if="getIndexOf(components, entry) > 0" style="position: absolute; left: 5px; top: 5px;" icon class="mmr-0" x-small color="#cccccc" @click.stop="moveEntryUp(entry)">
							<v-icon>mdi-arrow-up-bold</v-icon>
						</v-btn>
						<v-btn v-if="getIndexOf(components, entry) < components.length-1" style="position: absolute; left: 5px; top: 23px;" icon class="mmr-0" x-small color="#cccccc" @click.stop="moveEntryDown(entry)">
							<v-icon>mdi-arrow-down-bold</v-icon>
						</v-btn>
						<div class="font-weight-bold ml-3">{{entry.label}}</div><div style="position: absolute; top: 17px; left: 30%;">{{entry.name}}</div>
						<v-btn style="position: absolute; top: 14px; right: 80px;" icon class="mmr-0" x-small color="#aaaaaa" @click.stop="deleteEntry(entry)">
							<v-icon>mdi-trash-can</v-icon>
						</v-btn>
					</div>
				</v-expansion-panel-header>

				<!-- Expanded panel -->
				<v-expansion-panel-content eager>
					
					<!-- -------------------------------------------- -->
					<!--   Dynamic field components -->
					<!-- -------------------------------------------- -->

					<keep-alive>
						<component ref="dataTypeComponent" :field="entry" :is="entry.editorUiComponent" class="mt-8" />
					</keep-alive>
					
				</v-expansion-panel-content>
			</v-expansion-panel>

		</v-expansion-panels>

	</div>

</template>


<script>

// ----------------------------------------------------
//
//  Main script
//
// ----------------------------------------------------

import Vue from 'vue';
import agile from "@/shift/agile";

import ContentFieldTextField from '@/components/contenteditor/fields/ContentFieldTextField';
import ContentFieldTextArea from '@/components/contenteditor/fields/ContentFieldTextArea';
import ContentFieldNumber from '@/components/contenteditor/fields/ContentFieldNumber';
import ContentFieldInteger from '@/components/contenteditor/fields/ContentFieldInteger';
import ContentFieldBoolean from '@/components/contenteditor/fields/ContentFieldBoolean';
import ContentFieldTable from '@/components/contenteditor/fields/ContentFieldTable';
import ContentFieldRichTextEditor from '@/components/contenteditor/fields/ContentFieldRichTextEditor';
import ContentFieldSimpleFile from '@/components/contenteditor/fields/ContentFieldSimpleFile';
import ContentFieldSimpleImage from '@/components/contenteditor/fields/ContentFieldSimpleImage';
import ContentFieldSimpleVideo from '@/components/contenteditor/fields/ContentFieldSimpleVideo';

import ViewTextFieldVO from '@/valueobjects/viewseditor/ViewTextFieldVO';
import ViewTextAreaVO from '@/valueobjects/viewseditor/ViewTextAreaVO';
import ViewNumberVO from '@/valueobjects/viewseditor/ViewNumberVO';
import ViewIntegerVO from '@/valueobjects/viewseditor/ViewIntegerVO';
import ViewBooleanVO from '@/valueobjects/viewseditor/ViewBooleanVO';
import ViewTableVO from '@/valueobjects/viewseditor/ViewTableVO';
import ViewCustomObjectsVO from '@/valueobjects/viewseditor/ViewCustomObjectsVO';
import ViewRichTextEditorVO from '@/valueobjects/viewseditor/ViewRichTextEditorVO';
import ViewSimpleFileVO from '@/valueobjects/viewseditor/ViewSimpleFileVO';
import ViewSimpleImageVO from '@/valueobjects/viewseditor/ViewSimpleImageVO';
import ViewSimpleVideoVO from '@/valueobjects/viewseditor/ViewSimpleVideoVO';





export default
{
	name: 'ContentFieldDynamic',

	// ----------------------------------
	//	VUE: Data, properties, etc.
	// ----------------------------------

	props:
	{
		field: { type: Object },
	},

	data: () =>
	({
		fieldTypeToAdd: null,
		components: [],
	}),


	// ----------------------------------------------------
	//
	//  VUE: Create and Mount
	//
	// ----------------------------------------------------

	created()
	{
		
		// ----------------------------------------------------
		//	#Todo: For the field components, I have relied on 
		//	that 'value' is always bound, set and ready 
		//	whenever the document is saved. This 'dynamic 
		//	fields' component got a bit more complex though.
		//	Don't know how to make it properright now
		//  so I am making this hack now, and check for
		//	value function in the ContentEditPage save to
		//	firestore operation.
		// ----------------------------------------------------


		this.field.value = () =>
		{
			const returnArray = [];
			
			for (let i = 0; i < this.components.length; i++)
			{
				returnArray.push({
					value: this.components[i].value,
					fieldType: this.components[i].fieldType,
					label: this.components[i].label,
				});
			}
			return returnArray;
		};


		// ----------------------------------------------------
		//	Get saved data
		// ----------------------------------------------------

		// Do we have existing data?
		if (this.field.data)
		{
			for (let i = 0; i < this.field.data.length; i++)
			{
				this.createEntry(
					this.field.data[i].label,
					this.getFieldTypeByType(this.field.data[i].fieldType),
					this.field.data[i].value
				);
			}
		}

		
	},

	mounted()
	{
		
	},


	// ----------------------------------------------------
	//
	//  VUE: Methods
	//
	// ----------------------------------------------------

	methods:
	{
		addEntry()
		{
			this.$root.$setName('Name your new field') // Open dialog box to set name
			.then((nameReturned) =>
			{
				if (nameReturned)
				{
					this.createEntry(nameReturned, this.fieldTypeToAdd);
				}
			});
		},

		

		createEntry(label, fielTypeObject, fieldData = null)
		{
			
			const newEntry = Vue.observable(Object.assign({}, fielTypeObject)); // Copy field object
			newEntry.label = label;
			newEntry.template = {}; // We just need this to be compatible with dynamic values in the component
			newEntry.actualId = this.field.actualId;
			
			
			// ----------------------------------------------------
			//	Ge the actual components we gonna edit in the UI
			// ----------------------------------------------------

			let componentName;
			
			switch (newEntry.fieldType)
			{
				case 'textField': componentName = 'ContentFieldTextField'; break;
				case 'textArea': componentName = 'ContentFieldTextArea'; break;
				case 'number': componentName = 'ContentFieldNumber'; break;
				case 'integer': componentName = 'ContentFieldInteger'; break;
				case 'boolean': componentName = 'ContentFieldBoolean'; break;
				//case 'customObjects': componentName = 'ContentFieldCustomObjects'; break;
				case 'table': componentName = 'ContentFieldTable'; break;
				case 'richTextEditor': componentName = 'ContentFieldRichTextEditor'; break;
				case 'simpleFile': componentName = 'ContentFieldSimpleFile'; break;
				case 'dynamicField': componentName = 'ContentFieldDynamic'; break;
				case 'simpleImage': componentName = 'ContentFieldSimpleImage'; break;
				case 'simpleVideo': componentName = 'ContentFieldSimpleVideo'; break;
			}

			newEntry.editorUiComponent = componentName;


			// ----------------------------------------------------
			//	Get value object with default values for fields
			// ----------------------------------------------------

			let vo;

			switch (newEntry.fieldType)
			{
				case 'textField': vo = agile.convertPropertiesToPlainObject(new ViewTextFieldVO()); break;
				case 'textArea': vo = agile.convertPropertiesToPlainObject(new ViewTextAreaVO()); break;
				case 'boolean': vo = agile.convertPropertiesToPlainObject(new ViewBooleanVO()); break;
				case 'table': vo = agile.convertPropertiesToPlainObject(new ViewTableVO()); break;
				case 'number': vo = agile.convertPropertiesToPlainObject(new ViewNumberVO()); break;
				case 'integer': vo = agile.convertPropertiesToPlainObject(new ViewIntegerVO()); break;
				case 'customObjects': vo = agile.convertPropertiesToPlainObject(new ViewCustomObjectsVO()); break;
				case 'richTextEditor': vo = agile.convertPropertiesToPlainObject(new ViewRichTextEditorVO()); break;
				case 'simpleFile': vo = agile.convertPropertiesToPlainObject(new ViewSimpleFileVO()); break;
				case 'simpleImage': vo = agile.convertPropertiesToPlainObject(new ViewSimpleImageVO()); break;
				case 'simpleVideo': vo = agile.convertPropertiesToPlainObject(new ViewSimpleVideoVO()); break;
			}
			
			
			// If fieldData is not null, it means we are loading field data from existing databse
			if (fieldData)
			{
				newEntry.data = fieldData;
			}
			
			newEntry.template.typeProperties = Vue.observable(vo);
			

			// ----------------------------------------------------
			//	Hack the table component, so we can see all settings
			// ----------------------------------------------------

			this.components.push(newEntry);

			//this.field.value.push(newEntry.value);
				
		},

		moveEntryUp(entry)
		{
			const currentPos = this.getIndexOf(this.components, entry);

			if (currentPos > 0)
			{
				this.components.splice(currentPos, 1);
				this.components.splice(currentPos-1, 0, entry);
			}
		},

		moveEntryDown(entry)
		{
			
			const currentPos = this.getIndexOf(this.components, entry);

			if (currentPos < this.components.length)
			{
				this.components.splice(currentPos, 1);
				this.components.splice(currentPos+1, 0, entry);
			}
		},

		deleteEntry(entry)
		{
			this.$root.$confirm('Deleting', 'Do you really want to delete field?', { color: 'warning' } )
			.then((confirm) =>
			{
				if (confirm)
				{
					const currentPos = this.getIndexOf(this.components, entry);
					this.components.splice(currentPos, 1);
				}
			});
		},

		getIndexOf(array, object)
		{
			for (let i = 0; i < array.length; i++)
			{
				if (array[i] === object)
				{
					return i;
				}
			}
			return -1;
		},


		getFieldTypeByType(fieldType)
		{
			const fieldTypesAvail = this.field.template.typeProperties.fieldTypesAvailable;

			for (let i = 0; i < fieldTypesAvail.length; i++)
			{
				if (fieldType === fieldTypesAvail[i].fieldType) return fieldTypesAvail[i];
			}
			return null;
		},


	},

	components:
	{
		ContentFieldTextField,
		ContentFieldTextArea,
		ContentFieldNumber,
		ContentFieldInteger,
		ContentFieldBoolean,
		ContentFieldTable,
		ContentFieldRichTextEditor,
		ContentFieldSimpleFile,
		ContentFieldSimpleImage,
		ContentFieldSimpleVideo
	},

}

</script>

